/**
 * Color Match, in place. One file for both surfaces.
 *
 * The featured pages' Color Match band and the product page's Color Match
 * callout ask the same question — "which spool is closest to this color?" —
 * and the store owner asked for the answer on the page rather than one
 * navigation away. The matching itself is the live tool's
 * (sections/material-colour-match.liquid): the same input grammar, the same
 * Pantone resolution through window.MCM_PANTONE, the same ΔE2000 ranking of
 * window.MCM_COLOURS. Nothing here reimplements the color maths — the
 * distance, the Lab conversion and the tile painting are JessieColour's
 * (plan §3.4) and are called, never copied.
 *
 *   JessieMatch.parseInput(text)   '#1d3757' | '1D3757' | '#abc' | 'PANTONE
 *                                  7550 U' → { hex, label, kind }, or
 *                                  { error, reason } when it is none of those.
 *   JessieMatch.rank(hex, opts)    every window.MCM_COLOURS row scored by
 *                                  ΔE2000 against that hex, closest first,
 *                                  cut to opts.limit. Rows are clones.
 *   JessieMatch.render(root, text) parse, rank and paint one surface.
 *   JessieMatch.mount(root, opts)  wire a surface's input, color picker,
 *                                  Match button and hand-off link.
 *
 * The gate is unchanged and is still WP1's (assets/jessie-gate.js): one
 * unlock, in sessionStorage under the live tool's own global keys, shared by
 * every surface on every page. Locked is a *rendering* state here, exactly as
 * it is in the live tool — the ranking runs either way, but a locked surface
 * paints placeholder rows behind the "hidden until you enter your email"
 * overlay, so nothing that has not been unlocked is ever in the DOM. The
 * shopper's typed value still goes into `ps_cm_input` on every match, so the
 * full tool picks it up when they follow the link.
 *
 * ANALYTICS (2026-09-09). This file previously reported nothing, so matching
 * on product pages and feature pages was invisible while the shared gate was
 * already reporting signups. Events now fire from run() — the user-initiated
 * path only. render() is also called on mount and on every gate-unlock
 * repaint, and firing from there would count a page load as a use and inflate
 * the funnel. render() returns the parse and the top hit so run() can describe
 * what happened without ranking twice.
 *
 *   colour_match         a color was read and ranked
 *   colour_match_error   a color was typed and could not be read
 *
 * `surface` comes from the nearest [data-cm-surface] ancestor; the impression
 * event that pairs with these lives in snippets/ga4.liquid and keys off the
 * same attribute.
 *
 * Load order (all deferred, in this order): material-colours.js,
 * pantone-lookup.js, jessie-colour.js, this, then the surface's own script.
 *
 * Idempotent (plan §1.6 rule 7): both surfaces emit this src, so it can be on
 * a page twice, and a repeated src re-executes. The second execution re-runs
 * init() instead of re-registering anything, and mount() skips a root it has
 * already wired.
 */
if (window.__jessie_match) {
  /* Already loaded. A later section emits the src again; by then a surface
     that was not in the document on the first pass may be. */
  if (window.JessieMatch) window.JessieMatch.init();
} else {
  window.__jessie_match = true;

  (function () {
    'use strict';

    var MOUNTED = 'data-jessie-match-mounted';
    var DEFAULT_MESSAGE = 'Enter a hex like #1D3757 or a Pantone code';
    var DEFAULT_LIMIT = 5;
    var HEX_RE = /^(#[0-9a-f]{3}|#?[0-9a-f]{6})$/i;

    function jc() {
      return window.JessieColour || null;
    }

    function attr(el, name, fallback) {
      var value = el && el.getAttribute(name);
      return value == null || value === '' ? fallback : value;
    }

    function num(value, fallback) {
      var n = parseInt(value, 10);
      return isNaN(n) ? fallback : n;
    }

    function text(el, value) {
      if (el) el.textContent = value == null ? '' : String(value);
    }

    function show(el, visible) {
      if (el) el.hidden = !visible;
    }

    function all(root, selector) {
      return Array.prototype.slice.call((root || document).querySelectorAll(selector));
    }

    /* ----------------------------------------------------------- analytics */

    /** Which Colour Match surface this element sits in. */
    function surfaceOf(el) {
      var host = el && el.closest ? el.closest('[data-cm-surface]') : null;
      return (host && host.getAttribute('data-cm-surface')) || 'unknown';
    }

    function track(event, data) {
      try {
        window.dataLayer = window.dataLayer || [];
        var payload = { event: event };
        for (var key in data || {}) {
          if (Object.prototype.hasOwnProperty.call(data, key)) payload[key] = data[key];
        }
        window.dataLayer.push(payload);
      } catch (e) {
        /* Analytics must never break the surface. */
      }
    }

    /** The live tool's percentage: 100 minus ΔE, floored at 0. */
    function pct(de) {
      return Math.max(0, Math.round(100 - de));
    }

    /**
     * Report one user-initiated match.
     *
     * `state` is what render() returned. Nothing is sent for an empty field:
     * clearing the input is not a use of the tool.
     */
    function report(root, typed, state) {
      if (!state || state.status === 'empty') return;
      var surface = surfaceOf(root);

      if (state.status === 'error') {
        track('colour_match_error', {
          surface: surface,
          input_value: typed,
          reason: (state.parsed && state.parsed.reason) || 'unknown'
        });
        return;
      }

      var top = state.top && state.top.row;
      track('colour_match', {
        surface: surface,
        input_type: (state.parsed && state.parsed.kind) || '',
        input_value: typed,
        matched_brand: (top && top.brand) || '',
        matched_material: (top && top.material) || '',
        /* The MCM row id ('jessie-pla-blue-ice'), not the display name: a
           name can repeat across lines, and it is what the full tool and the
           click-through event report too. */
        matched_spool: (top && top.id) || '',
        match_pct: state.top ? pct(state.top.de) : null,
        locked: state.status === 'locked'
      });
    }

    /* ------------------------------------------------------------- parsing */

    /**
     * Pantone code → lookup key, exactly as the live tool normalises it:
     * case and the words PANTONE / PMS dropped, spaces and underscores to
     * hyphens, a trailing finish suffix split off and folded to c or u.
     */
    function normPantone(value) {
      var s = String(value).trim().toLowerCase().replace(/pantone|pms/g, '').trim().replace(/[\s_]+/g, '-');
      var m = s.match(/^(.*?)-?(xgc|cp|up|tpg|tpx|tcx|c|u)$/);
      var fin = '';
      if (m) {
        s = m[1];
        var f = m[2];
        fin = f === 'u' || f === 'up' ? 'u' : f === 'c' || f === 'cp' || f === 'xgc' ? 'c' : f;
      }
      return { base: s.replace(/^-+|-+$/g, ''), fin: fin };
    }

    /** '7550-u' → 'PANTONE 7550 U'; 'p-116-15-c' → 'PANTONE P 116-15 C'. */
    function prettyPantone(key) {
      var m = key.match(/^(.*?)(?:-(c|u))?$/);
      var base = m[1];
      var fin = m[2] ? ' ' + m[2].toUpperCase() : '';
      var pm = base.match(/^p-(\d+)-(\d+)$/);
      var body = pm
        ? 'P ' + pm[1] + '-' + pm[2]
        : base
            .split('-')
            .map(function (word) {
              return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
      return 'PANTONE ' + body + fin;
    }

    /**
     * The shopper's input, resolved to a hex.
     *
     * `reason` says which branch failed — 'empty', 'unknown' (not a hex and
     * not a Pantone shape) or 'pantone-missing' (a Pantone shape that is not
     * in the table) — and `error` carries the one message a shopper sees. The
     * surfaces override that message with their own setting; the reason is
     * for tests, for callers that want to react differently, and for the
     * unmet-demand report, where 'pantone-missing' is the interesting one.
     */
    function parseInput(raw) {
      var v = String(raw == null ? '' : raw).trim();
      if (!v) return { error: DEFAULT_MESSAGE, reason: 'empty' };

      if (HEX_RE.test(v)) {
        var h = v.replace('#', '');
        if (h.length === 3) {
          h = h
            .split('')
            .map(function (c) {
              return c + c;
            })
            .join('');
        }
        var hex = '#' + h.toUpperCase();
        return { hex: hex, label: hex, kind: 'hex' };
      }

      var p = normPantone(v);
      if (!p.base) return { error: DEFAULT_MESSAGE, reason: 'unknown' };

      var table = window.MCM_PANTONE;
      if (!table) return { error: DEFAULT_MESSAGE, reason: 'pantone-missing' };

      var keys = p.fin ? [p.base + '-' + p.fin] : [p.base + '-c', p.base + '-u', p.base];
      for (var i = 0; i < keys.length; i++) {
        if (table[keys[i]]) {
          return {
            hex: String(table[keys[i]]).toUpperCase(),
            label: prettyPantone(keys[i]),
            kind: 'pantone',
          };
        }
      }
      return { error: DEFAULT_MESSAGE, reason: 'pantone-missing' };
    }

    /* ------------------------------------------------------------- ranking */

    function isLab(value) {
      return (
        Object.prototype.toString.call(value) === '[object Array]' &&
        value.length === 3 &&
        typeof value[0] === 'number' &&
        typeof value[1] === 'number' &&
        typeof value[2] === 'number'
      );
    }

    /**
     * The row's Lab, without writing it back. The live tool back-fills `lab`
     * onto MCM_COLOURS entries; two consumers sharing that mutation is the bug
     * §3.4 warns about, so a computed value is cached by hex here instead.
     */
    function labOf(row, cache) {
      if (isLab(row.lab)) return row.lab;
      if (!row.hex) return null;
      var key = String(row.hex).toUpperCase();
      if (!cache[key]) cache[key] = jc().rgbToLab(jc().hexToRgb(key));
      return cache[key];
    }

    function clone(row) {
      var copy = {};
      for (var key in row) {
        if (Object.prototype.hasOwnProperty.call(row, key)) copy[key] = row[key];
      }
      return copy;
    }

    /**
     * Every spool we publish, ranked against one hex by ΔE2000.
     *
     * The whole table, brand and line included, because that is what the live
     * tool ranks and what the band's own copy promises. `opts.line` and
     * `opts.brand` narrow it where a surface wants to; `opts.limit` cuts the
     * list (0 or less returns all of it). Ties break on id so two runs on the
     * same table give the same order.
     */
    function rank(hex, opts) {
      opts = opts || {};
      var J = jc();
      var table = window.MCM_COLOURS;
      if (!J || !hex || !table || !table.length) return [];

      var lab = J.rgbToLab(J.hexToRgb(hex));
      var cache = {};
      var scored = [];
      for (var i = 0; i < table.length; i++) {
        var row = table[i];
        if (!row || !row.hex) continue;
        if (opts.line && row.line !== opts.line) continue;
        if (opts.brand && row.brand !== opts.brand) continue;
        var rowLab = labOf(row, cache);
        if (!rowLab) continue;
        scored.push({ row: row, de: J.deltaE2000(lab, rowLab) });
      }

      scored.sort(function (a, b) {
        if (a.de !== b.de) return a.de - b.de;
        return String(a.row.id) < String(b.row.id) ? -1 : 1;
      });

      var limit = opts.limit == null ? DEFAULT_LIMIT : opts.limit;
      var list = limit > 0 ? scored.slice(0, limit) : scored;
      return list.map(function (hit) {
        return { row: clone(hit.row), de: hit.de };
      });
    }

    /* ------------------------------------------------------------ rendering */

    function refs(root) {
      return {
        input: root.querySelector('[data-jessie-match-input]'),
        picker: root.querySelector('[data-jessie-match-picker]'),
        error: root.querySelector('[data-jessie-match-error]'),
        matches: root.querySelector('[data-jessie-match-matches]'),
        rows: root.querySelector('[data-jessie-match-rows]'),
        locked: root.querySelector('[data-jessie-match-locked]'),
        pending: root.querySelector('[data-jessie-match-pending]'),
        proto: root.querySelector('[data-jessie-match-tile]'),
      };
    }

    function options(root) {
      return {
        prefix: attr(root, 'data-jessie-match-prefix', 'jessie-match'),
        limit: num(attr(root, 'data-jessie-match-limit', ''), DEFAULT_LIMIT),
        deltaLabel: attr(root, 'data-jessie-match-delta-label', 'ΔE'),
        message: attr(root, 'data-jessie-match-error-text', DEFAULT_MESSAGE),
        line: attr(root, 'data-jessie-match-line', ''),
      };
    }

    function isUnlocked() {
      return !!(window.JessieGate && window.JessieGate.isUnlocked());
    }

    /** The section's own tile shell, cloned and painted; a dot if there is none. */
    function tileFor(proto, row, prefix) {
      if (proto && proto.content && proto.content.firstElementChild && jc()) {
        var tile = proto.content.firstElementChild.cloneNode(true);
        tile.setAttribute('data-jessie-tile', row.id);
        if (jc().paintTile(tile, row)) return tile;
      }
      var dot = document.createElement('span');
      dot.className = prefix + '__result-dot';
      dot.style.backgroundColor = row.hex;
      dot.setAttribute('aria-hidden', 'true');
      return dot;
    }

    /**
     * The line's display name for one result row.
     *
     * MCM's own `line_label` has no trademark symbol on Elixir, and the theme
     * settings own the Jessie names (plan §3.7, "Line display names"), so a
     * Jessie row takes JessieColour.lineLabel(row.line) — the same helper the
     * Color ID card, the story eyebrow and the mega menu strip already use.
     * Every other brand keeps the table's own label: a Prusament PLA row has
     * `line: 'PLA'` too, and passing that through lineLabel() would print
     * "Jessie Premium PLA" on a Prusament spool.
     */
    function lineLabelFor(row) {
      if (!row) return '';
      if (row.brand === 'Jessie' && jc() && jc().lineLabel) {
        var label = jc().lineLabel(row.line);
        if (label) return label;
      }
      return row.line_label || '';
    }

    function span(className, value) {
      var el = document.createElement('span');
      el.className = className;
      if (value != null) el.textContent = String(value);
      return el;
    }

    /** One real match: tile, name, line and hex, and the computed ΔE. */
    function resultRow(hit, opts, proto) {
      var row = hit.row;
      var item = document.createElement(row.url ? 'a' : 'div');
      item.className = opts.prefix + '__result';
      if (row.url) item.href = row.url;
      /* colour-match-attribution.js reads this on click-through so its
         matched_spool agrees with the one reported on colour_match. */
      if (row.id) item.setAttribute('data-jessie-match-row', row.id);

      var body = span(opts.prefix + '__result-body');
      body.appendChild(span(opts.prefix + '__result-name', row.name));
      var meta = [lineLabelFor(row), row.hex].filter(Boolean).join(' · ');
      body.appendChild(span(opts.prefix + '__result-meta', meta));

      item.appendChild(tileFor(proto, row, opts.prefix));
      item.appendChild(body);
      item.appendChild(
        span(opts.prefix + '__result-delta', (opts.deltaLabel + ' ' + (Math.round(hit.de * 10) / 10).toFixed(1)).trim())
      );
      return item;
    }

    /**
     * One locked match: the same shape, no data. The live tool draws the same
     * skeleton for its gated rows — the layout is honest about how many spools
     * were found without saying which they are, and there is nothing in the
     * markup to read before the email is in.
     */
    function lockedRow(opts) {
      var item = document.createElement('div');
      item.className = opts.prefix + '__result ' + opts.prefix + '__result--locked';
      item.setAttribute('aria-hidden', 'true');
      item.appendChild(span(opts.prefix + '__result-dot ' + opts.prefix + '__ph'));
      var body = span(opts.prefix + '__result-body');
      body.appendChild(span(opts.prefix + '__ph ' + opts.prefix + '__ph--name'));
      body.appendChild(span(opts.prefix + '__ph ' + opts.prefix + '__ph--meta'));
      item.appendChild(body);
      item.appendChild(span(opts.prefix + '__ph ' + opts.prefix + '__ph--delta'));
      return item;
    }

    /**
     * Parse `value`, rank it and paint `root`.
     *
     * Returns the state it painted: `{ status, hex, count, parsed, top }` with
     * status one of 'empty', 'error', 'locked' or 'ok'. `parsed` and `top` are
     * there so a caller can report what happened without ranking again; this
     * function itself sends nothing, because it also runs on mount and on
     * every gate-unlock repaint. Both surfaces call this, and neither one
     * knows what a ΔE is.
     */
    function render(root, value) {
      if (!root) return { status: 'empty', hex: null, count: 0, parsed: null, top: null };
      var el = refs(root);
      var opts = options(root);
      var unlocked = isUnlocked();

      var raw = String(value == null ? (el.input ? el.input.value : '') : value);
      var parsed = parseInput(raw);

      /* Nothing typed: no error, no rows, just the invitation to type. */
      if (parsed.reason === 'empty') {
        text(el.error, '');
        show(el.error, false);
        show(el.matches, false);
        show(el.locked, false);
        show(el.pending, true);
        return { status: 'empty', hex: null, count: 0, parsed: parsed, top: null };
      }

      /* Typed, but not a color we can read: say so and rank nothing. */
      if (parsed.error) {
        text(el.error, opts.message);
        show(el.error, true);
        show(el.matches, false);
        show(el.locked, false);
        show(el.pending, false);
        return { status: 'error', hex: null, count: 0, parsed: parsed, top: null };
      }

      text(el.error, '');
      show(el.error, false);

      var hits = rank(parsed.hex, { limit: opts.limit, line: opts.line || null });
      if (!hits.length) {
        /* No color table on the page: render nothing rather than an empty box. */
        show(el.matches, false);
        show(el.locked, false);
        show(el.pending, true);
        return { status: 'empty', hex: parsed.hex, count: 0, parsed: parsed, top: null };
      }

      if (el.picker && parsed.hex) el.picker.value = parsed.hex.toLowerCase();

      if (el.rows) {
        el.rows.innerHTML = '';
        for (var i = 0; i < hits.length; i++) {
          el.rows.appendChild(unlocked ? resultRow(hits[i], opts, el.proto) : lockedRow(opts));
        }
      }
      if (el.matches) {
        el.matches.classList.toggle('is-locked', !unlocked);
        el.matches.classList.toggle('is-unlocked', unlocked);
      }
      show(el.matches, true);
      show(el.locked, !unlocked);
      show(el.pending, false);

      return {
        status: unlocked ? 'ok' : 'locked',
        hex: parsed.hex,
        count: hits.length,
        parsed: parsed,
        top: hits[0]
      };
    }

    /* -------------------------------------------------------------- wiring */

    /**
     * Wire one surface. Idempotent: a root already mounted is left alone, so a
     * second copy of this file, or a second init() after an AJAX section swap,
     * costs nothing.
     */
    function mount(root) {
      if (!root || root.hasAttribute(MOUNTED)) return null;
      root.setAttribute(MOUNTED, '');
      var el = refs(root);

      function remember() {
        if (!window.JessieGate || !el.input) return;
        var typed = String(el.input.value || '').trim();
        if (typed) window.JessieGate.setInput(typed);
      }

      /* The user-initiated path, and the only one that reports. */
      function run() {
        remember();
        var typed = String(el.input ? el.input.value : '').trim();
        var state = render(root, el.input ? el.input.value : '');
        report(root, typed, state);
        return state;
      }

      var go = root.querySelector('[data-jessie-match-go]');
      if (go) {
        go.addEventListener('click', function (event) {
          event.preventDefault();
          run();
        });
      }

      if (el.input) {
        el.input.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          run();
        });
        /* Typing clears the message but keeps the last matches on screen, the
           way the live tool does: results change when the shopper asks. */
        el.input.addEventListener('input', function () {
          text(el.error, '');
          show(el.error, false);
        });
      }

      if (el.picker) {
        el.picker.addEventListener('change', function () {
          var hex = String(el.picker.value || '').toUpperCase();
          if (el.input) el.input.value = hex;
          run();
        });
      }

      /* The hand-off to the full tool. It reads the typed color from
         sessionStorage on load and has no query string of its own. */
      all(root, '[data-jessie-match-open]').forEach(function (link) {
        link.addEventListener('click', remember);
      });

      /* Someone unlocked — here, or in another gate on the page. Repaint, and
         report nothing: the unlock is the gate's own event, and this repaint
         is not a new use of the tool. */
      window.addEventListener('jessie:gate-unlocked', function () {
        render(root, el.input ? el.input.value : '');
      });

      /* What the shopper typed on the last surface, so the two agree. */
      if (el.input && !el.input.value && window.JessieGate) {
        var stored = window.JessieGate.input();
        if (stored) el.input.value = stored;
      }

      render(root, el.input ? el.input.value : '');
      /* `run`, not `render`: this one reports. A caller that only wants a
         repaint uses JessieMatch.render(root), as the surface scripts do. */
      return { root: root, run: run };
    }

    function init() {
      all(document, '[data-jessie-match]').forEach(function (root) {
        try {
          mount(root);
        } catch (error) {
          /* One broken surface must not take the rest of the page with it. */
          if (window.console && console.warn) console.warn('[jessie-match]', error);
        }
      });
    }

    window.JessieMatch = {
      DEFAULT_MESSAGE: DEFAULT_MESSAGE,
      parseInput: parseInput,
      rank: rank,
      render: render,
      mount: mount,
      init: init,
      isUnlocked: isUnlocked,
      surfaceOf: surfaceOf,
    };

    /* The two surface scripts call mount() themselves once the colour table is
       ready; this is the safety net for a surface whose own script never ran. */
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    window.addEventListener('product:section-swapped', init);
  })();
}
